import { useState, useEffect, useCallback } from "react";
import { formatDate, formatDateTime } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Pencil, Trash2, Eye, Lock, Square, Car, Info, UserRound, Home, ChevronRight, Save } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

/* ---------------- SCHEMA ---------------- */

const creditCustomerSchema = z.object({
  registered_date: z.string().min(1, "Registered date is required"),
  organization_name: z.string().min(1, "Organization name is required"),
  tin_gst_no: z.string().optional(),
  representative_name: z.string().optional(),
  organization_address: z.string().optional(),
  advance_no: z.string().optional(),
  phone_number: z.string().refine((val) => !val || val.length === 10, {
    message: "Phone number must be exactly 10 digits"
  }).optional(),
  alt_phone_no: z.string().refine((val) => !val || val.length === 10, {
    message: "Alternate phone number must be exactly 10 digits"
  }).optional(),
  credit_limit: z.coerce.number().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  email: z.string().email("Invalid email format").optional().or(z.literal("")),
  opening_balance: z.coerce.number().optional(),
  opening_date: z.string().optional(),
  balance_type: z.string().optional(),
  discount_amount: z.coerce.number().optional(),
  offer_type: z.string().optional(),
  vehicle_no: z.string().optional(),
  vehicle_type: z.string().optional(),
  customer_type: z.string().optional(),
  penalty_interest: z.any().optional(),
  run_interest: z.string().optional(),
  grace_days: z.coerce.number().optional(),
  interest_percentage: z.coerce.number().optional(),
  image_url: z.string().optional(),
});

type CreditCustomerForm = z.infer<typeof creditCustomerSchema>;

const getDefaultCreditCustomerValues = (): CreditCustomerForm => ({
  registered_date: new Date().toISOString().split("T")[0],
  opening_date: new Date().toISOString().split("T")[0],
  organization_name: "",
  tin_gst_no: "",
  representative_name: "",
  organization_address: "",
  advance_no: "",
  phone_number: "",
  alt_phone_no: "",
  credit_limit: 0,
  username: "",
  password: "",
  email: "",
  opening_balance: 0,
  balance_type: "Credit",
  discount_amount: 0,
  offer_type: "Per 1 ltr",
  vehicle_no: "",
  vehicle_type: "",
  customer_type: "N/A",
  penalty_interest: false,
  run_interest: "No",
  grace_days: 0,
  interest_percentage: 0,
  image_url: "",
});

type CustomerVehicle = {
  vehicleNo: string;
  vehicleType: string;
};

const VEHICLE_TYPES = [
  "Ambulance",
  "Auto",
  "Barrel",
  "Bike",
  "Bus",
  "Cab",
  "Can",
  "Car",
  "Eicher",
  "JCB",
  "Jeep",
  "Mini Lorry",
  "Sumo",
  "Tractor",
  "Truck",
  "Van",
];

const normalizeVehicles = (customer: any): CustomerVehicle[] => {
  if (Array.isArray(customer?.vehicles) && customer.vehicles.length > 0) {
    return customer.vehicles.map((vehicle: any) => ({
      vehicleNo: vehicle.vehicleNo || vehicle.vehicle_no || "",
      vehicleType: vehicle.vehicleType || vehicle.vehicle_type || "Car",
    }));
  }

  if (customer?.vehicle_no || customer?.vehicle_type) {
    return [{
      vehicleNo: customer.vehicle_no || "",
      vehicleType: customer.vehicle_type || "Car",
    }];
  }

  return [];
};


/* ---------------- COMPONENT ---------------- */

export default function CreditCustomers() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showRunInterest, setShowRunInterest] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCount, setShowCount] = useState(10);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [balanceType, setBalanceType] = useState("Credit");
  const [offerType, setOfferType] = useState("Per 1 ltr");
  const [vehicleType, setVehicleType] = useState("");
  const [viewingCustomer, setViewingCustomer] = useState<any | null>(null);
  const [vehicleCustomer, setVehicleCustomer] = useState<any | null>(null);
  const [vehicleRows, setVehicleRows] = useState<CustomerVehicle[]>([]);
  const [statusFilter, setStatusFilter] = useState<"Active" | "Inactive" | "All">("All");

  const form = useForm<CreditCustomerForm>({
    resolver: zodResolver(creditCustomerSchema),
    defaultValues: getDefaultCreditCustomerValues(),
    shouldFocusError: false,
  });

  /* ---------------- FETCH ---------------- */

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch("/api/credit-customers", {
        credentials: "include",
      });
      const data = await res.json();
      if (data.ok) setCustomers(data.rows || []);
      else toast({ variant: "destructive", title: "Error", description: data.error || "Failed to fetch customers" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to fetch customers" });
    }
  }, [toast]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  /* ---------------- SUBMIT ---------------- */

  const onSubmit = async (data: CreditCustomerForm) => {
    const payload = {
      ...data,
      representative_name: data.representative_name?.trim() || data.organization_name,
      image_url: uploadedImage || data.image_url || "",
      mobile_number: data.phone_number,
      balance_type: data.balance_type === "Credit" ? "Due" : "Excess",
    };

    try {
      const url = editingId
        ? `/api/credit-customers/${editingId}`
        : "/api/credit-customers";
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.ok) {
        toast({
          title: "Success",
          description: editingId ? "Updated successfully" : "Saved successfully"
        });
        form.reset(getDefaultCreditCustomerValues());
        setEditingId(null);
        setShowRunInterest(false);
        setUploadedImage(null);
        setBalanceType("Credit");
        setOfferType("Per 1 ltr");
        setVehicleType("");
        fetchCustomers();
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to save",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save credit customer",
        variant: "destructive"
      });
    }
  };

  const onInvalidSubmit = (errors: typeof form.formState.errors) => {
    const firstError = Object.values(errors)[0];
    toast({
      title: "Check required fields",
      description: firstError?.message?.toString() || "Please enter organization name and valid contact details.",
      variant: "destructive",
    });
  };

  /* ---------------- EDIT ---------------- */

  const handleEdit = (customer: any) => {
    setEditingId(customer.id);
    form.reset({
      registered_date: customer.registered_date || new Date().toISOString().split("T")[0],
      organization_name: customer.organization_name || "",
      tin_gst_no: customer.tin_gst_no || "",
      representative_name: customer.representative_name || "",
      organization_address: customer.organization_address || "",
      advance_no: customer.advance_no || "",
      phone_number: customer.phone_number || customer.mobile_number || "",
      alt_phone_no: customer.alt_phone_no || "",
      credit_limit: customer.credit_limit || 0,
      username: customer.username || "",
      password: customer.password || "",
      email: customer.email || "",
      opening_balance: customer.opening_balance || 0,
      opening_date: customer.opening_date || new Date().toISOString().split("T")[0],
      balance_type: customer.balance_type || "Credit",
      discount_amount: customer.discount_amount || 0,
      offer_type: customer.offer_type || "Per 1 ltr",
      vehicle_no: customer.vehicle_no || "",
      vehicle_type: customer.vehicle_type || "",
      customer_type: customer.customer_type || "N/A",
      penalty_interest: customer.penalty_interest || false,
      run_interest: customer.run_interest || "No",
      grace_days: customer.grace_days || 0,
      interest_percentage: customer.interest_percentage || 0,
      image_url: customer.image_url || "",
    });

    setUploadedImage(customer.image_url || null);
    setBalanceType(customer.balance_type || "Credit");
    setOfferType(customer.offer_type || "Per 1 ltr");
    setVehicleType(customer.vehicle_type || "");

    if (customer.penalty_interest) {
      setShowRunInterest(true);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    const res = await fetch(`/api/credit-customers/${deleteConfirm}`, {
      method: "DELETE",
      credentials: "include",
    });

    const result = await res.json();

    if (result.ok) {
      toast({ title: "Success", description: "Deleted successfully" });
      setDeleteConfirm(null);
      fetchCustomers();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete",
        variant: "destructive"
      });
    }
  };

  /* ---------------- VEHICLES ---------------- */

  const openVehicles = (customer: any) => {
    setVehicleCustomer(customer);
    const rows = normalizeVehicles(customer);
    setVehicleRows(rows.length > 0 ? rows : [{ vehicleNo: "", vehicleType: "Car" }]);
  };

  const updateVehicleRow = (index: number, key: keyof CustomerVehicle, value: string) => {
    setVehicleRows((rows) => rows.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [key]: value } : row
    )));
  };

  const addVehicleRow = () => {
    setVehicleRows((rows) => [...rows, { vehicleNo: "", vehicleType: "Car" }]);
  };

  const removeVehicleRow = (index: number) => {
    setVehicleRows((rows) => rows.length === 1 ? [{ vehicleNo: "", vehicleType: "Car" }] : rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const saveVehicles = async () => {
    if (!vehicleCustomer) return;

    const vehicles = vehicleRows
      .map((row) => ({
        vehicleNo: row.vehicleNo.trim(),
        vehicleType: row.vehicleType || "Car",
      }))
      .filter((row) => row.vehicleNo || row.vehicleType);

    const primaryVehicle = vehicles[0] || { vehicleNo: "", vehicleType: "" };
    const res = await fetch(`/api/credit-customers/${vehicleCustomer.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        vehicles,
        vehicle_no: primaryVehicle.vehicleNo,
        vehicle_type: primaryVehicle.vehicleType,
      }),
    });

    const result = await res.json();
    if (result.ok) {
      toast({ title: "Success", description: "Vehicles updated successfully" });
      setVehicleCustomer(null);
      setVehicleRows([]);
      fetchCustomers();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to update vehicles",
        variant: "destructive",
      });
    }
  };

  /* ---------------- TOGGLE STATUS ---------------- */

  const toggleStatus = async (customerId: string, currentStatus: boolean) => {
    const res = await fetch(`/api/credit-customers/${customerId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ is_active: !currentStatus }),
    });

    const result = await res.json();

    if (result.ok) {
      toast({
        title: "Success",
        description: `Customer ${!currentStatus ? 'activated' : 'deactivated'} successfully`
      });
      fetchCustomers();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to update status",
        variant: "destructive"
      });
    }
  };

  /* ---------------- IMAGE UPLOAD ---------------- */

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setUploadedImage(base64String);
        form.setValue("image_url", base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  /* ---------------- FILTERING ---------------- */

  const filteredCustomers = customers
    .filter((customer) => {
      // Search filter
      const query = searchQuery.trim().toLowerCase();
      const matchesSearch = !query || (
        customer.organization_name?.toLowerCase().includes(query) ||
        customer.representative_name?.toLowerCase().includes(query) ||
        customer.phone_number?.toLowerCase().includes(query) ||
        customer.mobile_number?.toLowerCase().includes(query) ||
        customer.email?.toLowerCase().includes(query) ||
        customer.tin_gst_no?.toLowerCase().includes(query)
      );

      if (!matchesSearch) return false;

      // Status filter
      if (statusFilter === "Active") return customer.is_active !== false;
      if (statusFilter === "Inactive") return customer.is_active === false;
      return true;
    });

  const paginatedCustomers = filteredCustomers.slice(0, showCount === 999999 ? filteredCustomers.length : showCount);

  /* ---------------- UI ---------------- */

  return (
    <div className="space-y-4">

      <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">
        <Home className="h-4 w-4" />
        <span>Dashboard</span>
        <ChevronRight className="h-4 w-4" />
        <span className="font-normal">Customer Entry</span>
      </div>

      <form
        onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}
        className="rounded-md bg-gradient-to-r from-blue-500 to-blue-800 px-5 py-7 text-white shadow-sm"
      >
        <h2 className="mb-12 text-2xl font-bold">
          {editingId ? "Edit Credit Customer" : "Create Credit Customer"}
        </h2>

        <div className="grid grid-cols-4 gap-2">
          <div>
            <Input type="date" {...form.register("registered_date")} className="h-10 bg-white text-sm text-black" />
            {form.formState.errors.registered_date && (
              <p className="text-[10px] text-red-100 mt-0.5">{form.formState.errors.registered_date.message}</p>
            )}
          </div>
          <div>
            <Input placeholder="Organization Name*" {...form.register("organization_name")} className="h-10 bg-white text-sm text-black" />
            {form.formState.errors.organization_name && (
              <p className="text-[10px] text-red-100 mt-0.5">{form.formState.errors.organization_name.message}</p>
            )}
          </div>
          <Input placeholder="TIN / GST NO" {...form.register("tin_gst_no")} className="h-10 bg-white text-sm text-black" />
          <div>
            <Input placeholder="Cust/Represent Name" {...form.register("representative_name")} className="h-10 bg-white text-sm text-black" />
            {form.formState.errors.representative_name && (
              <p className="text-[10px] text-red-100 mt-0.5">{form.formState.errors.representative_name.message}</p>
            )}
          </div>
        </div>

        <div className="mt-7 grid grid-cols-[1fr_1fr_1.4fr_.65fr] gap-2">
          <Input placeholder="Organization Address" {...form.register("organization_address")} className="h-10 bg-white text-sm text-black" />
          <Input placeholder="Advance Rs/-" {...form.register("advance_no")} className="h-10 bg-white text-sm text-black" />
          <div>
            <Label className="mb-1 block text-sm font-semibold text-white">Upload Image</Label>
            <Input
              type="file"
              className="h-10 bg-white text-sm text-black file:mr-4 file:h-10 file:border-0 file:bg-blue-400 file:px-4 file:text-white"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/tiff"
              onChange={handleImageUpload}
            />
            <p className="mt-1 text-xs text-cyan-200">Allowed (JPEG, JPG, TIF, GIF, PNG) MaxSize:2MB</p>
          </div>
          <div className="pt-6">
            <Input placeholder="Phone No" {...form.register("phone_number")} className="h-10 bg-white text-sm text-black" maxLength={10} type="tel" />
            {form.formState.errors.phone_number && (
              <p className="text-[10px] text-red-100 mt-0.5">{form.formState.errors.phone_number.message}</p>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-[1.5fr_1.1fr_1fr_1fr_1.1fr] gap-2">
          <div>
            <Input placeholder="Alt Phone No" {...form.register("alt_phone_no")} className="h-10 bg-white text-sm text-black" maxLength={10} type="tel" />
            {form.formState.errors.alt_phone_no && (
              <p className="text-[10px] text-red-100 mt-0.5">{form.formState.errors.alt_phone_no.message}</p>
            )}
          </div>
          <Input type="number" placeholder="Credit Limit" {...form.register("credit_limit")} className="h-10 bg-white text-sm text-black" />
          <Input placeholder="User Name" {...form.register("username")} className="h-10 bg-white text-sm text-black" />
          <Input type="password" placeholder="Password" {...form.register("password")} className="h-10 bg-white text-sm text-black" />
          <div>
            <Label className="mb-1 block text-sm font-semibold text-white">Customer Type</Label>
            <Select value={form.watch("customer_type") || "N/A"} onValueChange={(v) => form.setValue("customer_type", v)}>
              <SelectTrigger className="h-10 bg-white text-sm text-black">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="N/A">N/A</SelectItem>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="Qty">Qty</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-9 grid grid-cols-[.95fr_2.25fr_.58fr] items-center gap-4">
          <div>
            <Label className="mb-2 block text-sm font-semibold text-white">Email</Label>
            <Input placeholder="" type="email" {...form.register("email")} className="h-10 bg-white text-sm text-black" />
            {form.formState.errors.email && (
              <p className="text-[10px] text-red-100 mt-0.5">{form.formState.errors.email.message}</p>
            )}
          </div>

          <div className="rounded-md border border-blue-800/70 p-3 shadow-sm">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="mb-2 block text-sm font-semibold text-white">Opening Balance (₹)</Label>
                <Input type="number" placeholder="0" {...form.register("opening_balance")} className="h-10 bg-white text-sm text-black" />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-white">Opening Date</Label>
                <Input type="date" {...form.register("opening_date")} className="h-10 bg-white text-sm text-black" />
                <p className="mt-3 text-xs font-semibold text-cyan-200">Cutoff Date (Ex:31-Mar-202X)</p>
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-white">Balance Type</Label>
                <Select
                  value={balanceType}
                  onValueChange={(v) => {
                    setBalanceType(v);
                    form.setValue("balance_type", v);
                  }}
                >
                  <SelectTrigger className="h-10 bg-white text-sm text-black">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Credit">Due : Will Receive</SelectItem>
                    <SelectItem value="Debit">Excess : Will Payback</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-3 text-xs font-semibold leading-5 text-cyan-200">
                  Due : Will Receive<br />Excess : Will Payback
                </p>
              </div>
            </div>
          </div>

          <label htmlFor="penalty_interest" className="mt-7 flex h-[72px] cursor-pointer items-center justify-center gap-4 rounded-md bg-white px-4 text-sm font-bold text-black shadow-lg">
            <input
              type="checkbox"
              id="penalty_interest"
              {...form.register("penalty_interest")}
              onChange={(e) => setShowRunInterest(e.target.checked)}
              className="h-6 w-6 accent-cyan-900"
            />
            Penality Interest
          </label>
        </div>

            {showRunInterest && (
              <div className="mt-4 rounded bg-blue-900/20 p-3">
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold text-white">Run Interest</Label>
                    <div className="flex gap-3 mt-1">
                      <div className="flex items-center space-x-1">
                        <input type="radio" id="ryes" value="Yes" {...form.register("run_interest")} className="h-3 w-3" />
                        <Label htmlFor="ryes" className="text-[10px]">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <input type="radio" id="rno" value="No" {...form.register("run_interest")} className="h-3 w-3" defaultChecked />
                        <Label htmlFor="rno" className="text-[10px]">No</Label>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-5">
                    <Label className="text-xs font-semibold text-white">Grace days</Label>
                    <Input type="number" {...form.register("grace_days")} className="h-8 text-xs mt-0.5" />
                  </div>
                  <div className="col-span-5">
                    <Label className="text-xs font-semibold text-white">Interest (%)</Label>
                    <Input type="number" step="0.01" {...form.register("interest_percentage")} className="h-8 text-xs mt-0.5" />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 grid grid-cols-[260px_260px_1fr] items-end gap-3">
              <div>
                <Label className="mb-2 block text-sm font-semibold text-white">Discount Amount</Label>
                <Input type="number" {...form.register("discount_amount")} className="h-10 bg-white text-sm text-black" />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-white">Offer Type</Label>
                <Select value={offerType} onValueChange={(v) => { setOfferType(v); form.setValue("offer_type", v); }}>
                  <SelectTrigger className="h-10 bg-white text-sm text-black">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Per 1 ltr">Per 1 ltr</SelectItem>
                    <SelectItem value="Flat">Flat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-[260px_260px_auto_1fr] items-end gap-3">
              <div>
                <Label className="mb-2 block text-sm font-semibold text-white">Vehicle No</Label>
                <Input placeholder="Vehicle No" {...form.register("vehicle_no")} className="h-10 bg-white text-sm text-black" />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-white">Vehicle Type</Label>
                <Select value={vehicleType || undefined} onValueChange={(v) => { setVehicleType(v); form.setValue("vehicle_type", v); }}>
                  <SelectTrigger className="h-10 bg-white text-sm text-black">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-white hover:bg-white/10 hover:text-white" title="Add vehicle details">
                <Plus className="h-6 w-6 rounded-full border-2 border-white" />
              </Button>
            </div>

        <div className="mt-11 flex justify-center">
          <Button type="submit" className="h-10 rounded-full bg-[#9be457] px-8 text-sm font-bold text-black hover:bg-[#8bd642]">
            <Save className="h-4 w-4" />
            {editingId ? "UPDATE" : "SAVE"}
          </Button>
        </div>
      </form>

      <Card>
        <div className="p-3 bg-white border-b flex justify-between items-center bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold">Show:</span>
            <Select value={String(showCount)} onValueChange={(v) => setShowCount(Number(v))}>
              <SelectTrigger className="w-20 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="999999">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Search className="h-3 w-3 text-gray-400" />
            <Input
              placeholder="Search customers..."
              className="h-7 w-48 text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-100">
              <TableRow>
                <TableHead className="font-bold text-[11px] h-8">S.No</TableHead>
                <TableHead className="font-bold text-[11px] h-8 text-center">Action</TableHead>
                <TableHead className="font-bold text-[11px] h-8 text-center">Status</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Date</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Organization</TableHead>
                <TableHead className="font-bold text-[11px] h-8">GST NO</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Phone Number</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Discount</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Opening Details</TableHead>
                <TableHead className="font-bold text-[11px] h-8 text-right">Advance</TableHead>
                <TableHead className="font-bold text-[11px] h-8 text-right">Limit</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Email</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Address</TableHead>
                <TableHead className="font-bold text-[11px] h-8">Represent</TableHead>
                <TableHead className="font-bold text-[11px] h-8 text-center">Image</TableHead>
                <TableHead className="font-bold text-[11px] h-8 text-center">Vehicles</TableHead>
                <TableHead className="font-bold text-[11px] h-8">App Login</TableHead>
                <TableHead className="font-bold text-[11px] h-8">User Log Details</TableHead>
                <TableHead className="font-bold text-[11px] h-8 text-center">More Info</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={19} className="text-center py-6 text-xs text-gray-500">No customers found.</TableCell>
                </TableRow>
              ) : (
                paginatedCustomers.map((customer, index) => (
                  <TableRow key={customer.id} className="hover:bg-gray-50 h-10">
                    <TableCell className="text-xs">{index + 1}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" onClick={() => handleEdit(customer)} title="Edit customer">
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => setDeleteConfirm(customer.id)} title="Delete customer">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => toggleStatus(customer.id, customer.is_active !== false)}
                          className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${customer.is_active !== false ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${customer.is_active !== false ? 'translate-x-7' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-[9px] font-bold ${customer.is_active !== false ? 'text-green-600' : 'text-gray-500'}`}>
                          {customer.is_active !== false ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDate(customer.registered_date || customer.registeredDate || customer.created_at || customer.createdAt)}</TableCell>
                    <TableCell className="text-xs font-medium text-blue-900">{customer.organization_name}</TableCell>
                    <TableCell className="text-xs">{customer.tin_gst_no || "-"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div>Phone: {customer.mobile_number || customer.phone_number || "-"}</div>
                      <div>Alt No: {customer.alt_phone_no || "-"}</div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div>Offer: {customer.discount_amount || 0}</div>
                      <div>Type: {customer.offer_type || "-"}</div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {Number(customer.opening_balance || 0) > 0 ? (
                        <>
                          <div>Balance : {parseFloat(customer.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div>Date : {customer.opening_date || "-"}</div>
                          <div>Type : {customer.balance_type || "-"}</div>
                        </>
                      ) : "N/A"}
                    </TableCell>
                    <TableCell className="text-xs text-right">{parseFloat(customer.advance_no || customer.advance_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-xs text-right">{parseFloat(customer.credit_limit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-xs">{customer.email || "-"}</TableCell>
                    <TableCell className="text-xs min-w-40">{customer.organization_address || customer.address || "-"}</TableCell>
                    <TableCell className="text-xs">{customer.representative_name || "-"}</TableCell>
                    <TableCell className="text-center">
                      {customer.image_url || customer.image ? (
                        <img src={customer.image_url || customer.image} alt={customer.organization_name} className="mx-auto h-8 w-8 rounded object-cover" />
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-green-700" onClick={() => navigate(`/master/credit-customer/${customer.id}/vehicles`)} title="Open customer vehicles">
                        <Car className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div>User Name : {customer.username || ""}</div>
                      <div>Password : {customer.password || ""}</div>
                    </TableCell>
                    <TableCell className="text-xs min-w-48">
                      <div>Created: {customer.created_by || customer.createdBy || "Super Admin"} {formatDateTime(customer.created_at || customer.createdAt)}</div>
                      {(customer.updated_at || customer.updatedAt) && (
                        <div>Modified: {formatDateTime(customer.updated_at || customer.updatedAt)}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-700" onClick={() => setViewingCustomer(customer)} title="View more info">
                        <Info className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={vehicleCustomer !== null} onOpenChange={(open) => {
        if (!open) {
          setVehicleCustomer(null);
          setVehicleRows([]);
        }
      }}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="bg-blue-700 px-5 py-3 text-white">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Car className="h-4 w-4" />
              Add Vehicles
            </DialogTitle>
            <DialogDescription className="text-blue-100">
              {vehicleCustomer?.organization_name || vehicleCustomer?.organizationName || ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 p-5">
            {vehicleRows.map((row, index) => (
              <div key={index} className="grid grid-cols-[1fr_1fr_auto_auto] items-end gap-3">
                <div>
                  <Label className="text-xs font-semibold">Vehicle No</Label>
                  <Input
                    value={row.vehicleNo}
                    onChange={(event) => updateVehicleRow(index, "vehicleNo", event.target.value)}
                    placeholder="Vehicle No"
                    className="mt-1 h-9 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Vehicle Type</Label>
                  <Select
                    value={row.vehicleType || "Car"}
                    onValueChange={(value) => updateVehicleRow(index, "vehicleType", value)}
                  >
                    <SelectTrigger className="mt-1 h-9 text-xs">
                      <SelectValue placeholder="Vehicle Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {VEHICLE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" size="icon" className="h-9 w-9" onClick={addVehicleRow} title="Add vehicle row">
                  <Plus className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-600" onClick={() => removeVehicleRow(index)} title="Remove vehicle row">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <div className="flex justify-center gap-2 pt-2">
              <Button type="button" className="h-8 bg-green-600 px-5 text-xs hover:bg-green-700" onClick={saveVehicles}>
                Submit
              </Button>
              <Button type="button" className="h-8 bg-red-600 px-5 text-xs hover:bg-red-700" onClick={() => setVehicleCustomer(null)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={viewingCustomer !== null} onOpenChange={(open) => !open && setViewingCustomer(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserRound className="h-4 w-4" />
              Customer Details
            </DialogTitle>
            <DialogDescription>
              {viewingCustomer?.organization_name || viewingCustomer?.organizationName || ""}
            </DialogDescription>
          </DialogHeader>
          {viewingCustomer && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="font-semibold">GST:</span> {viewingCustomer.tin_gst_no || "-"}</div>
              <div><span className="font-semibold">Phone:</span> {viewingCustomer.mobile_number || viewingCustomer.phone_number || "-"}</div>
              <div><span className="font-semibold">Alt No:</span> {viewingCustomer.alt_phone_no || "-"}</div>
              <div><span className="font-semibold">Email:</span> {viewingCustomer.email || "-"}</div>
              <div><span className="font-semibold">Credit Limit:</span> {parseFloat(viewingCustomer.credit_limit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div><span className="font-semibold">Opening Balance:</span> {parseFloat(viewingCustomer.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="col-span-2"><span className="font-semibold">Address:</span> {viewingCustomer.organization_address || viewingCustomer.address || "-"}</div>
              <div className="col-span-2">
                <span className="font-semibold">Vehicles:</span>{" "}
                {normalizeVehicles(viewingCustomer).map((vehicle) => `${vehicle.vehicleNo || "0"} (${vehicle.vehicleType})`).join(", ") || "-"}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will permanently delete this customer. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

